import { Link } from "react-router-dom";
import { useT } from "@/lib/i18n";
import { reviewUspText } from "@/lib/reviews";
import { kiyohScoreLabel, useKiyohStats } from "@/lib/kiyoh-stats";

const Check = ({ className }: { className: string }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" className={`shrink-0 ${className}`}>
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

/** USP's als inline rij met gekleurde vinkjes (geen blokken). Elke USP linkt naar
 *  de pagina die 'm onderbouwt (ervaring → over-ons, levering → levertijden, enz.). */
const USPS: { check: string; lead?: string; text: string; href: string; review?: true }[] = [
  { check: "text-accent-500", lead: "120+", text: "jaar ervaring", href: "/over-ons" },
  { check: "text-success-500", text: "Snelle en veilige levering", href: "/levertijden" },
  { check: "text-primary-600", text: "Maatwerk in een paar klikken", href: "/glassoorten" },
  { check: "text-primary-600", text: reviewUspText, href: "/reviews", review: true },
];

const List = () => {
  const t = useT();
  // Zodra Kiyoh een cijfer heeft, wordt de review-USP "Beoordeeld met een 9,4" — anders blijft de
  // neutrale tekst staan. Zo hoeft niemand een cijfer met de hand bij te werken.
  const stats = useKiyohStats();
  const uspTekst = (u: (typeof USPS)[number]): string =>
    u.review && stats ? `${t("Beoordeeld met een")} ${kiyohScoreLabel(stats.average)}` : t(u.text);
  return (
    <ul className="mx-auto flex max-w-[1600px] flex-wrap items-center justify-center gap-x-6 gap-y-2 px-4 text-sm sm:gap-x-8">
      {/* Mobiel: één USP (Coolblue-stijl, rustiger); vanaf sm allemaal. */}
      {USPS.map((u, i) => (
        <li key={u.text} className={`${i === 0 ? "flex" : "hidden sm:flex"} items-center gap-2 whitespace-nowrap`}>
          <Link to={u.href} className="flex items-center gap-2 rounded transition hover:opacity-80 focus-visible:outline focus-visible:outline-2 focus-visible:outline-accent-500">
            <Check className={u.check} />
            <span className="font-bold text-primary-800">
              {u.lead && <span className="text-accent-700">{u.lead} </span>}
              {uspTekst(u)}
            </span>
          </Link>
        </li>
      ))}
    </ul>
  );
};

/**
 * USP-rij. `inline` (default) = dunne rij onder de hero. `band` = volledige
 * band met achtergrond + randen, voor boven de footer (sitebreed).
 */
export function UspRow({ variant = "inline" }: { variant?: "inline" | "band" }) {
  const t = useT();
  if (variant === "band") {
    return (
      <section aria-label={t("Onze beloftes")} className="mt-8 border-y border-primary-100 bg-white py-4 sm:mt-16 sm:py-5">
        <List />
      </section>
    );
  }
  return (
    <section aria-label={t("Onze beloftes")} className="mt-8 sm:mt-14">
      <List />
    </section>
  );
}
